import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { Link } from "react-router";
import { api, type Category } from "@/api";
import { CLOTHES_DEPARTMENT, megaColumns } from "@/lib/category-tree";
import type { Department } from "@/lib/home-feed";

// Колонки мега-меню строятся из дерева категорий API (дерево v2, три уровня):
// «Одежда» — корни-аудитории (Женщинам / Мужчинам / Детям), прочие разделы
// («Красота», «Дом и интерьер») — свой корень. Ни одного id в коде.

type Tile = {
  id: string;
  slug: string;
  title: string;
  cover: string;
};

// Строка списка категорий: 38px (pad 10/0), Manrope 400/14 #161616.
const ROW_CLASS = "flex h-[38px] items-center text-[14px] leading-[18px] text-[#161616] transition-colors hover:text-osmix-tangelo";
// Заголовок группы второго уровня («Одежда», «Обувь»): 600/14, листья — под ним.
const GROUP_CLASS = "flex h-[34px] items-center text-[14px] font-semibold leading-[18px] text-black transition-colors hover:text-osmix-tangelo";
// Лист третьего уровня: 400/13 приглушённый, с отступом под группой.
const LEAF_CLASS = "flex h-[28px] items-center pl-3 text-[13px] leading-[16px] text-osmix-text-2 transition-colors hover:text-osmix-tangelo";
const catalogHref = (id: string) => `/catalog?categoryId=${encodeURIComponent(id)}`;

// Группа второго уровня со своими листьями. Картинка группы — imageUrl
// категории (в консоли), без неё — текстовая строка, как раньше.
function GroupLinks(t0) {
  const $ = _c(19);
  const {
    node,
    leaves,
    onNavigate
  } = t0;
  const t1 = node.id;
  let t2;
  if ($[0] !== node.id) {
    t2 = catalogHref(node.id);
    $[0] = node.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = leaves.length ? GROUP_CLASS : ROW_CLASS;
  let t4;
  if ($[2] !== node.imageUrl) {
    t4 = node.imageUrl && <img src={node.imageUrl} alt="" loading="lazy" className="mr-2 h-6 w-10 rounded-[4px] object-cover" />;
    $[2] = node.imageUrl;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== node.name || $[5] !== onNavigate || $[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <Link to={t2} onClick={onNavigate} className={t3}>{t4}{node.name}</Link>;
    $[4] = node.name;
    $[5] = onNavigate;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== leaves || $[11] !== onNavigate) {
    let t7;
    if ($[13] !== onNavigate) {
      t7 = leaf => <Link key={leaf.id} to={catalogHref(leaf.id)} onClick={onNavigate} className={LEAF_CLASS}>{leaf.name}</Link>;
      $[13] = onNavigate;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    t6 = leaves.map(t7);
    $[10] = leaves;
    $[11] = onNavigate;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[15] !== node.id || $[16] !== t5 || $[17] !== t6) {
    t7 = <div className="flex flex-col" data-mega-group={t1}>{t5}{t6}</div>;
    $[15] = node.id;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}

// «Новинки» — ряд из трёх плиток-подборок (кадр «Ховер»): подпись 400/14, под ней
// через 12px три плитки 109×137 r4 с зазором 10 и подписью 400/12 через 6px.
// Берём первые подборки ленты вдохновения — один общий набор (в макете под
// каждой колонкой одни и те же «Подборки»).
function NoveltyTiles(t0) {
  const $ = _c(8);
  const {
    tiles,
    onNavigate
  } = t0;
  if (tiles.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="text-[14px] leading-[18px] text-[#161616]">Новинки</span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onNavigate || $[2] !== tiles) {
    let t3;
    if ($[4] !== onNavigate) {
      t3 = t => <Link key={t.id} to={`/feed/${t.slug}`} onClick={onNavigate} className="group flex flex-col gap-1.5"><div className="aspect-[109/137] w-full overflow-hidden rounded-[4px] bg-osmix-gray-100">{t.cover && <img src={t.cover} alt={t.title} loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />}</div><span className="line-clamp-1 text-[12px] leading-[15px] text-black transition-colors group-hover:text-osmix-tangelo">{t.title}</span></Link>;
      $[4] = onNavigate;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = tiles.map(t3);
    $[1] = onNavigate;
    $[2] = tiles;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <div className="flex max-w-[347px] flex-col gap-3 py-2.5">{t1}<div className="grid grid-cols-3 gap-2.5">{t2}</div></div>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function IdeasLink(t0) {
  const $ = _c(3);
  const {
    onNavigate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Star className="size-4 fill-osmix-tangelo text-osmix-tangelo" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onNavigate) {
    t2 = <Link to="/ideas" onClick={onNavigate} className={`${ROW_CLASS} gap-1.5`}>{t1}Идеи для вдохновения</Link>;
    $[1] = onNavigate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// Раскрывающаяся панель категорий (белая, тень в SiteHeader) — навигация по
// разделу. Отступы панели 16/30/30/30.
//   • «Одежда»: колонки 367px с зазором 20 — заголовок корня 600/20, «Новинки»,
//     «Все товары», группы второго уровня (Одежда / Обувь / Аксессуары) с
//     листьями третьего уровня, «Идеи для вдохновения» (звезда tangelo).
//   • Прочие разделы («Красота», «Дом и интерьер»): «Новинки» один раз сверху,
//     ниже — «Все товары» и сетка из трёх колонок с подкатегориями корня.
export function DepartmentMega(t0) {
  const $ = _c(16);
  const {
    department,
    categories,
    onNavigate
  } = t0;
  const columns = megaColumns(categories, department);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["feed", "collections"],
      queryFn: _temp
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const collections = useQuery(t1);
  let t2;
  if ($[1] !== collections.data) {
    t2 = (collections.data ?? []).slice(0, 3).map(_temp2);
    $[1] = collections.data;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tiles = t2;
  if (department !== CLOTHES_DEPARTMENT) {
    const column = columns[0];
    let t3;
    if ($[3] !== onNavigate || $[4] !== tiles) {
      t3 = <NoveltyTiles tiles={tiles} onNavigate={onNavigate} />;
      $[3] = onNavigate;
      $[4] = tiles;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== onNavigate) {
      t4 = g => <GroupLinks key={g.node.id} node={g.node} leaves={g.leaves} onNavigate={onNavigate} />;
      $[6] = onNavigate;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== onNavigate) {
      t5 = <IdeasLink onNavigate={onNavigate} />;
      $[8] = onNavigate;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return <div data-mega={department} className="mx-auto flex w-full max-w-[1440px] flex-col gap-5 px-4 pt-4 pb-[30px] md:px-8">{t3}<div className="grid grid-cols-3 gap-x-5">{column && <Link to={catalogHref(column.root.id)} onClick={onNavigate} className={ROW_CLASS}>Все товары</Link>}{(column?.groups ?? []).map(t4)}{t5}</div></div>;
  }
  const t3 = "mx-auto grid w-full max-w-[1440px] grid-cols-3 gap-5 px-4 pt-4 pb-[30px] md:px-8";
  let t4;
  if ($[10] !== onNavigate || $[11] !== tiles) {
    t4 = col => <div key={col.root.id} className="flex flex-col" data-mega-root={col.root.id}><h3 className="flex h-7 items-start pb-[5px] text-[20px] font-semibold leading-[23px] text-black">{col.root.name}</h3><NoveltyTiles tiles={tiles} onNavigate={onNavigate} /><div className="flex flex-col"><Link to={catalogHref(col.root.id)} onClick={onNavigate} className={ROW_CLASS}>Все товары</Link>{col.groups.map(g_0 => <GroupLinks key={g_0.node.id} node={g_0.node} leaves={g_0.leaves} onNavigate={onNavigate} />)}<IdeasLink onNavigate={onNavigate} /></div></div>;
    $[10] = onNavigate;
    $[11] = tiles;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = columns.map(t4);
  let t6;
  if ($[13] !== department || $[14] !== t5) {
    t6 = <div data-mega={department} className={t3}>{t5}</div>;
    $[13] = department;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp2(c) {
  return {
    id: c.id,
    slug: c.slug,
    title: c.title,
    cover: c.cover
  };
}
function _temp() {
  return api.feed.collections();
}